import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Loader2, Search, ImageOff, ShoppingCart } from "lucide-react";

import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter,
} from "../ui/dialog";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../ui/select";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { getPublicFileUrl } from "../../config";
import { formatarBRL } from "../../helpers/dinheiro";
import PrecoDoProduto from "../PrecoDoProduto";
import SelosDeFarmacia from "../SelosDeFarmacia";
import useProdutosPaginados from "../../hooks/useProdutosPaginados";

// O mesmo teto do backend (MAXIMO_POR_ENVIO). Aqui ele serve para avisar ANTES
// de a pessoa montar uma lista de vinte itens e levar erro no botão.
const MAXIMO = 10;

// Escolhe produtos do catálogo e manda pela conversa: uma foto com legenda por
// produto. Produto sem foto vai como texto — não é pulado.
const EnviarCatalogoModal = ({
	open,
	onClose,
	ticketId,
	contactId,
	onAdicionadoAoCarrinho,
}) => {
	const [categorias, setCategorias] = useState([]);
	// Modelos da legenda. "padrao" = sem modelo, o formato de fábrica.
	const [modelos, setModelos] = useState([]);
	const [modelo, setModelo] = useState("padrao");
	const [escolhidos, setEscolhidos] = useState([]);
	const [busca, setBusca] = useState("");
	const [categoria, setCategoria] = useState("todas");
	const [enviando, setEnviando] = useState(false);
	const [adicionando, setAdicionando] = useState(false);
	// Rascunhos abertos do contato, quando há mais de um e é preciso perguntar.
	const [escolhendoRascunho, setEscolhendoRascunho] = useState(null);

	// Paginado: com catálogo grande, a lista mostrava só os 20 primeiros.
	const {
		produtos,
		carregando,
		aoRolar,
	} = useProdutosPaginados(
		{
			searchParam: busca,
			onlyActive: "true",
			...(categoria !== "todas" ? { categoryId: categoria } : {}),
		},
		{ ativo: open },
	);

	useEffect(() => {
		if (!open) return;
		setEscolhidos([]);

		(async () => {
			try {
				const { data } = await api.get("/product-categories");
				setCategorias(Array.isArray(data) ? data : []);
			} catch {
				// Sem categorias a busca por nome continua funcionando.
			}

			try {
				const { data } = await api.get("/templates", {
					params: { kind: "catalogo" },
				});
				const lista = Array.isArray(data) ? data : [];

				setModelos(lista);
				// Nasce no padrão da empresa; sem padrão, "formato de fábrica".
				setModelo(String(lista.find((m) => m.isDefault)?.id || "padrao"));
			} catch {
				// Sem modelos o envio sai no formato de fábrica, como sempre saiu.
			}
		})();
	}, [open]);

	const alternar = (id) =>
		setEscolhidos((atual) => {
			if (atual.includes(id)) return atual.filter((x) => x !== id);
			if (atual.length >= MAXIMO) {
				toast.warning(
					`No máximo ${MAXIMO} produtos por envio — mais que isso o WhatsApp trata como spam.`,
				);
				return atual;
			}
			return [...atual, id];
		});

	const enviar = async () => {
		setEnviando(true);
		try {
			await api.post("/catalog/send", {
				ticketId,
				productIds: escolhidos,
				// "padrao" é a escolha explícita de NÃO usar modelo: o envio sai no
				// formato de fábrica mesmo que a empresa tenha um padrão marcado.
				...(modelo && modelo !== "padrao" ? { templateId: Number(modelo) } : {}),
			});
			toast.success(
				"Catálogo no departamento. As fotos saem uma a uma e aparecem na conversa.",
			);
			onClose();
		} catch (err) {
			toastError(err);
		}
		setEnviando(false);
	};

	// Mostrou o catálogo, o cliente escolheu: o passo seguinte é o orçamento, e
	// redigitar os mesmos produtos seria trabalho repetido.
	const adicionarNoOrcamento = async (rascunho) => {
		setAdicionando(true);
		try {
			let orcamento = rascunho;

			if (!orcamento) {
				const criado = await api.post("/quotes", { contactId, source: "conversa" });
				orcamento = criado.data;
			}

			// Um item por vez: o backend devolve o orçamento inteiro a cada
			// adição, e mandar em paralelo faria os totais correrem uns por cima
			// dos outros.
			for (const productId of escolhidos) {
				// eslint-disable-next-line no-await-in-loop
				await api.post(`/quotes/${orcamento.id}/items`, {
					productId,
					quantity: 1,
				});
			}

			toast.success(
				`${escolhidos.length} item(ns) no orçamento nº ${orcamento.number}.`,
			);
			onAdicionadoAoCarrinho?.();
			onClose();
		} catch (err) {
			toastError(err);
		}
		setAdicionando(false);
	};

	const adicionarAoCarrinho = async () => {
		setAdicionando(true);
		try {
			const { data } = await api.get("/quotes", {
				params: { contactId, status: "draft" },
			});

			const rascunhos = data.quotes || [];

			// Mais de um rascunho aberto: perguntar em qual, em vez de escolher
			// o primeiro em silêncio — produto na venda errada é raro e caro.
			if (rascunhos.length > 1) {
				setEscolhendoRascunho(rascunhos);
				setAdicionando(false);
				return;
			}

			await adicionarNoOrcamento(rascunhos[0]);
			return;
		} catch (err) {
			toastError(err);
		}
		setAdicionando(false);
	};

	const capaDe = (produto) => {
		const primeira = [...(produto.images || [])].sort(
			(a, b) => (a.position || 0) - (b.position || 0),
		)[0];
		return primeira?.item?.mediaUrl
			? getPublicFileUrl(primeira.item.mediaUrl)
			: null;
	};

	return (
		<Dialog open={open} onOpenChange={(v) => !v && onClose()}>
			<DialogContent className="sm:max-w-lg">
				<DialogHeader>
					<DialogTitle>Enviar catálogo</DialogTitle>
					<DialogDescription>
						Cada produto vai como uma foto com nome, preço e descrição. Produto
						sem foto chega como texto.
					</DialogDescription>
				</DialogHeader>

				<div className="flex flex-wrap gap-2">
					<div className="relative min-w-40 flex-1">
						<Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
						<Input
							value={busca}
							onChange={(e) => setBusca(e.target.value)}
							placeholder="Buscar produto"
							className="pl-9"
						/>
					</div>
					<Select value={modelo} onValueChange={setModelo}>
						<SelectTrigger className="w-44" title="Modelo da legenda">
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="padrao">Formato de fábrica</SelectItem>
							{modelos.map((m) => (
								<SelectItem key={m.id} value={String(m.id)}>
									{m.name}
									{m.isDefault ? " (padrão)" : ""}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
					<Select value={categoria} onValueChange={setCategoria}>
						<SelectTrigger className="w-44">
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="todas">Todas</SelectItem>
							{categorias.map((c) => (
								<SelectItem key={c.id} value={String(c.id)}>
									{c.name}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>

				{carregando && produtos.length === 0 ? (
					<div className="flex justify-center py-10">
						<Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
					</div>
				) : produtos.length === 0 ? (
					<p className="py-10 text-center text-sm text-muted-foreground">
						Nenhum produto ativo. Cadastre no Catálogo antes de mandar.
					</p>
				) : (
					<div className="max-h-[45vh] space-y-1.5 overflow-y-auto" onScroll={aoRolar}>
						{produtos.map((produto) => {
							const marcado = escolhidos.includes(produto.id);
							return (
								<button
									key={produto.id}
									type="button"
									onClick={() => alternar(produto.id)}
									className={`flex w-full items-center gap-3 rounded-lg border p-2 text-left transition-colors hover:bg-accent ${
										marcado ? "border-primary ring-1 ring-primary" : ""
									}`}
								>
									<div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded border bg-muted">
										{capaDe(produto) ? (
											<img
												src={capaDe(produto)}
												alt={produto.name}
												className="max-h-10 max-w-full object-contain"
											/>
										) : (
											<ImageOff className="h-4 w-4 text-muted-foreground" />
										)}
									</div>
									<div className="min-w-0 flex-1">
										<p className="flex items-center gap-1.5 text-sm font-medium">
											<span className="truncate">{produto.name}</span>
											<SelosDeFarmacia produto={produto} className="shrink-0" />
										</p>
										<p className="truncate text-xs text-muted-foreground">
											<PrecoDoProduto produto={produto} />
											{capaDe(produto) ? "" : " · sem foto, vai como texto"}
											{produto.trackStock &&
												(() => {
													const disponivel =
														Number(produto.stockBalance) -
														Number(produto.stockReserved || 0);

													return (
														<span
															className={
																disponivel <= 0 ? "text-destructive" : undefined
															}
														>
															{disponivel <= 0
																? " · sem estoque"
																: ` · ${disponivel.toLocaleString("pt-BR", {
																		maximumFractionDigits: 3,
																	})} disponíveis`}
														</span>
													);
												})()}
										</p>
									</div>
								</button>
							);
						})}
					</div>
				)}

				{escolhendoRascunho && (
					<div className="space-y-2 rounded-lg border bg-muted/40 p-3">
						<p className="text-xs text-muted-foreground">
							Este cliente tem mais de um orçamento em rascunho. Em qual
							adicionar?
						</p>
						<div className="flex flex-wrap gap-2">
							{escolhendoRascunho.map((o) => (
								<Button
									key={o.id}
									size="sm"
									variant="outline"
									disabled={adicionando}
									onClick={() => {
										setEscolhendoRascunho(null);
										adicionarNoOrcamento(o);
									}}
								>
									nº {o.number} — {formatarBRL(o.total)}
								</Button>
							))}
							<Button
								size="sm"
								variant="ghost"
								onClick={() => setEscolhendoRascunho(null)}
							>
								Cancelar
							</Button>
						</div>
					</div>
				)}

				<DialogFooter className="items-center sm:justify-between">
					<span className="text-xs text-muted-foreground">
						{escolhidos.length} de {MAXIMO} escolhidos
					</span>
					<div className="flex gap-2">
						<Button
							variant="outline"
							onClick={onClose}
							disabled={enviando || adicionando}
						>
							Cancelar
						</Button>
						{contactId && (
							<Button
								variant="outline"
								onClick={adicionarAoCarrinho}
								disabled={
									enviando || adicionando || escolhidos.length === 0
								}
							>
								{adicionando ? (
									<Loader2 className="h-4 w-4 animate-spin" />
								) : (
									<ShoppingCart className="h-4 w-4" />
								)}
								Adicionar ao orçamento
							</Button>
						)}
						<Button
							onClick={enviar}
							disabled={enviando || adicionando || escolhidos.length === 0}
						>
							{enviando && <Loader2 className="h-4 w-4 animate-spin" />}
							Enviar
						</Button>
					</div>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};

export default EnviarCatalogoModal;
